import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from './SiteSettingsContext';
import AuthPageShell from './components/AuthPageShell';

export default function ForgotPasswordPage() {
  const { settings: site } = useSiteSettings();
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    setBusy(true);
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : 'https://salonidesigns.com';
      const r = await fetch('/api/customer.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'request_password_reset',
          email: email.trim(),
          site_base_url: base,
        }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not send reset email.');
        return;
      }
      setMsg(d.message || 'If an account exists for that email, we sent reset instructions.');
    } catch {
      setError('Could not send request. Try WhatsApp or email us directly.');
    } finally {
      setBusy(false);
    }
  };

  const auth = site.auth_copy || {};
  const whatsappHref = `${site.whatsapp_href || 'https://wa.me/919680384332'}?text=${encodeURIComponent(
    auth.forgot_whatsapp_prefill || 'Hi, I need help resetting my Saloni Designs account password.'
  )}`;

  return (
    <AuthPageShell
      pageTitle="Reset password"
      asideTitle={auth.forgot_aside_title || 'Reset your password'}
      asideText={
        auth.forgot_aside_text ||
        'We will email you a secure link to choose a new password. The link expires after one hour.'
      }
      footer={
        <div className="d-flex flex-column gap-2 text-center">
          <a
            href={whatsappHref}
            className="btn btn-outline-secondary rounded-pill"
            target="_blank"
            rel="noopener noreferrer"
          >
            Message on WhatsApp
          </a>
          <Link to="/login" className="btn btn-link">
            Back to sign in
          </Link>
        </div>
      }
    >
      <div className="store-auth-card__mobile-brand d-lg-none text-center mb-3">
        <img src="/saloni-logo.png" alt="" width={48} height={48} className="rounded-circle mb-2" />
        <p className="small text-muted mb-0">Saloni Designs</p>
      </div>
      <h2 className="store-auth-card__title h4">Reset your password</h2>
      <p className="store-auth-card__subtitle small text-muted mb-3">
        {auth.forgot_card_subtitle ||
          'Enter your account email and we will send a link to choose a new password.'}
      </p>
      <form onSubmit={onSubmit} noValidate>
        <div className="checkout-field mb-3">
          <label className="checkout-field__label" htmlFor="reset-email">
            Email
          </label>
          <input
            id="reset-email"
            type="email"
            className="form-control checkout-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>
        {error ? (
          <div className="alert alert-danger py-2 small" role="alert">
            {error}
          </div>
        ) : null}
        {msg ? (
          <div className="alert alert-success py-2 small" role="status">
            {msg}
          </div>
        ) : null}
        <button type="submit" className="btn btn-primary rounded-pill w-100 store-cta" disabled={busy}>
          {busy ? 'Sending…' : 'Email reset link'}
        </button>
      </form>
      <div className="alert alert-light border small mt-4 mb-0 text-start" role="note">
        <p className="fw-semibold text-body mb-2">Email not working or no link arrived?</p>
        <ul className="mb-0 ps-3">
          <li>
            <Link to="/track-order">Track your order</Link> with order number + checkout email — no password.
          </li>
          <li>
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer">
              WhatsApp us
            </a>{' '}
            — we can reset your password manually.
          </li>
        </ul>
      </div>
    </AuthPageShell>
  );
}
